import { useEffect, useRef, useState } from "react";
import { DASH } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal markdown renderer (headings + bullets + bold)
function renderMd(text) {
  const lines = text.split("\n");
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1.5 mb-3 text-[#1A1B1A]">
          {listBuffer.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };
  const bold = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (line.startsWith("### ")) {
      flushList();
      out.push(
        <h4 key={`h-${idx}`} className="font-display text-lg tracking-tight text-[#0F3524] mt-5 mb-2">
          {line.replace("### ", "")}
        </h4>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      listBuffer.push(bold(line.substring(2)));
    } else if (!line) {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={`p-${idx}`} className="text-sm leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: bold(line) }} />
      );
    }
  });
  flushList();
  return out;
}

export default function AIInsights({
  hasBills,
  refreshKey,
  endpointPath = "/api/insights/stream",
  overline = "Editorial · AI-generated",
  title = "The narrator's take",
  subtitle = "Claude Sonnet 4.5 reads your bills, so you don't have to.",
  testid,
  refreshTestid,
  emptyText = "Upload some bills first — I'll narrate what I see.",
}) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | streaming | done | error
  const esRef = useRef(null);

  const startStream = () => {
    if (esRef.current) esRef.current.close();
    if (!hasBills) {
      setText(emptyText);
      setStatus("done");
      return;
    }
    setText("");
    setStatus("streaming");

    // We use cookie auth via credentials + backend also accepts ?token=
    const es = new EventSource(`${BACKEND_URL}${endpointPath}`, { withCredentials: true });
    esRef.current = es;
    es.onmessage = (ev) => {
      if (ev.data === "[DONE]") {
        setStatus("done");
        es.close();
        return;
      }
      // decode our \n escapes
      const chunk = ev.data.replace(/\\n/g, "\n");
      setText((prev) => prev + chunk);
    };
    es.onerror = () => {
      setStatus("error");
      es.close();
    };
  };

  useEffect(() => {
    startStream();
    return () => esRef.current?.close();
  }, [refreshKey]);

  return (
    <div
      data-testid={testid || DASH.aiInsights}
      className="flat-card p-6 sm:p-8 border-l-4 border-l-[#0F3524]"
    >
      <div className="flex items-start justify-between gap-6 mb-4">
        <div>
          <div className="overline mb-2">{overline}</div>
          <h3 className="font-display text-2xl tracking-tight">{title}</h3>
          <p className="text-sm text-[#6E716C] mt-1 max-w-md">{subtitle}</p>
        </div>
        <button
          data-testid={refreshTestid || DASH.refreshInsightsBtn}
          onClick={startStream}
          className="text-xs font-mono-data uppercase tracking-widest border border-[#E5E5DE] px-4 py-2 hover:border-[#0F3524] transition-colors whitespace-nowrap"
          disabled={status === "streaming"}
        >
          {status === "streaming" ? "Writing…" : "Regenerate"}
        </button>
      </div>

      <div className="min-h-[240px]">
        {text ? (
          renderMd(text)
        ) : (
          <div className="font-mono-data text-sm text-[#6E716C]">
            {status === "error" ? "Could not load insights." : "Preparing analysis…"}
          </div>
        )}
        {status === "streaming" && (
          <span className="inline-block w-2 h-4 bg-[#0F3524] align-middle animate-pulse ml-1" />
        )}
      </div>
    </div>
  );
}
